/**
 * Housing Unit Detail Footer DOM Component
 *
 * Renders the footer with pricing information and CTA buttons.
 * Displays a fixed footer pinned to the bottom of the detail sheet.
 *
 * @module nativeInteractiveSiteMap/components/housingUnitDetailFooter.dom
 */

import fastdom from 'fastdom';
import getLeadLotAttributes from '../services/leadLotAttributesService';

/**
 * Renders the "Price" display block for housing units.
 * @param {Object} housingUnit
 * @returns {string} HTML string.
 */
function getPricingDisplay(housingUnit) {
  const displayPrice = housingUnit.price ? `$${housingUnit.price.toLocaleString()}` : null;

  return `
    <div class="nhs-u-display--flex nhs-u-items-center nhs-u-justify-content-space-between nhs-u-gap--200">
      <span class="nhs-mq-native-interactive-site-map__footer-label-large nhs-o-typography--sans-body-1-small-regular nhs-u-color--black-1000">
        Price
      </span>
      <span class="nhs-mq-native-interactive-site-map__footer-label-large nhs-o-typography--sans-body-1-medium-regular nhs-u-color--black-1000">
        ${displayPrice ?? ''}
      </span>
    </div>
  `;
}

/**
 * Renders the CTA buttons section. The "View more details" link is conditionally
 * rendered only if housingUnit.listingUrlPath is available — the backend (see
 * NativeInteractiveSiteMapService.GetLotMapInventoryAsync) already resolves it to an absolute
 * URL by prefixing Configuration.NewHomeSourceDomain, the same domain-source pattern used by
 * server-rendered cards (e.g. AppCode/Services/CommunityCardService.cs), so no client-side URL
 * building is needed here.
 * @param {Object} housingUnit
 * @param {Object} community
 * @returns {string} HTML string.
 */
function getCtaButtonsDisplay(housingUnit, community, lot, structuralOptions) {
  const detailUrl = housingUnit?.listingUrlPath;
  const { lotNumber, lotSize, lotType, lotFacing } = getLeadLotAttributes(lot);
  // A housing unit (spec/QMI/Model home) is a concrete built home with a single fixed elevation —
  // unlike a floor plan, there's no elevation selector to pick from (see
  // components/elevationSelector.dom.js, only used by lotAndElevationFloorPlan.dom.js).
  const elevationTitle = housingUnit?.elevations?.[0]?.title ?? null;

  // These data-* attributes are read by leadGenerators/components/wrapper.js's global click
  // delegation as the clicked element's own dataset, then spread into taskFlow.js's
  // initialState() — which is how uiState.modalInformation ends up with the id/type/community
  // context GetMoreInformationForm (NHS20-8178) needs to post a real lead. 'spec' matches
  // leadConstructor's 'spec-information' key (services/leadConstructor.js), which reads
  // formData.id as models/specInformationLead.js's specId. housingUnit.specificationId is the
  // backend spec-home id (from processLotMapInventory.js's unit.SpecificationId) — distinct from
  // housingUnit.housingUnitId, which is the lot's FK to this unit, not the lead-posting id. The
  // lot/elevation attributes feed the Lead API's LotNumber/LotSize/LotType/LotFacing/Elevation
  // parameters (NHS20-8974).
  return `
    <div class="nhs-u-display--flex nhs-u-gap--500">
      ${detailUrl ? `
        <a class="nhs-c-button nhs-c-button--medium-40 nhs-c-button--primary-white nhs-u-flex--1 nhs-u-text--align-center"
          href="${detailUrl}" target="_blank" rel="noopener noreferrer">
          View more details
        </a>
      ` : ''}
      <button class="nhs-c-button nhs-c-button--medium-40 nhs-c-button--primary-lead nhs-u-flex--1" type="button"
        data-lead-generator-opener="request-information"
        data-from-page="CommunityDetail.NativeInteractiveSiteMap.Spec"
        data-id="${housingUnit.specificationId ?? ''}"
        data-type="spec"
        data-community-id="${community?.communityId ?? ''}"
        data-community-name="${community?.communityName ?? ''}"
        data-market-id="${community?.marketId ?? ''}"
        data-market-name="${community?.marketName ?? ''}"
        data-brand-name="${community?.brandName ?? ''}"
        data-lot-number="${lotNumber ?? ''}"
        data-lot-size="${lotSize ?? ''}"
        data-lot-type="${lotType ?? ''}"
        data-lot-facing="${lotFacing ?? ''}"
        data-elevation="${elevationTitle ?? ''}"
        data-structural-options="${structuralOptions ?? ''}"
      >
        Contact builder
      </button>
    </div>
  `;
}

/**
 * Creates the Housing Unit Detail Footer component.
 * @param {Object} housingUnit - The currently selected housing unit with pricing
 * @param {Object} community - The community context data
 * @param {Object} [lot] - The currently selected lot, if any (NHS20-8974 Lead API params).
 * @param {string|null} [structuralOptions] - The structural options currently selected, pipe
 *   delimited, if any. No selector exists yet to source this from — always null until one is
 *   built; the plumbing below is ready to carry a real value through to the Lead API as soon
 *   as it is.
 * @param {Object} [options]
 * @param {boolean} [options.isModel] - NHS20-9017: Model home detail pages show no price row at
 *   all in the footer, just the CTA buttons.
 * @returns {{ getElement: Function, remove: Function }}
 */
export default function createHousingUnitDetailFooterDom(
  housingUnit = null,
  community = null,
  lot = null,
  structuralOptions = null,
  { isModel = false } = {},
) {
  const state = { root: null };

  function initializeDom() {
    state.root = document.createElement('div');
    state.root.className = 'nhs-c-native-interactive-site-map__floorplan-detail-footer';
    state.root.setAttribute('data-card', '');

    if (!housingUnit) {
      return;
    }

    state.root.innerHTML = `
      <div class="nhs-u-flex-column nhs-u-gap--500 nhs-u-width--full">
        ${isModel ? '' : getPricingDisplay(housingUnit)}
        ${getCtaButtonsDisplay(housingUnit, community, lot, structuralOptions)}
      </div>
    `;
  }

  initializeDom();

  return {
    getElement() {
      return state.root;
    },

    remove() {
      fastdom.mutate(() => {
        state.root?.remove();
        state.root = null;
      });
    },
  };
}
